import type { Guest } from '../types/Guests';

/**
 * The guest list's Side filter. Side lives in the guest's `side` field (not in
 * tags), so the options are the side values that actually exist in the
 * project, plus "All sides" and "No side".
 */

export const ALL_SIDES = 'All';
export const NO_SIDE = '__none__';

export interface SideOption {
  value: string;
  label: string;
}

type WithSide = Pick<Guest, 'side'> | { side?: string | null };

/** The side value, trimmed and lowercased; null when the guest has none. */
export function normalizeSide(raw: string | null | undefined): string | null {
  const value = (raw ?? '').trim().toLowerCase();
  return value === '' ? null : value;
}

/** bride -> "Bride's side", groom -> "Groom's side", both -> "Both sides", anything else title-cased ("alfa-grace" -> "Alfa-Grace"). */
export function sideLabel(value: string): string {
  if (value === 'bride') return "Bride's side";
  if (value === 'groom') return "Groom's side";
  if (value === 'both') return 'Both sides';
  return value.replace(/(^|[-\s])([a-z])/g, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
}

const PINNED_ORDER = ['bride', 'groom'];

export function buildSideOptions(guests: readonly WithSide[]): SideOption[] {
  const present = new Set<string>();
  for (const guest of guests) {
    const side = normalizeSide(guest.side as string | null | undefined);
    if (side) present.add(side);
  }
  const pinned = PINNED_ORDER.filter(v => present.has(v));
  const others = Array.from(present)
    .filter(v => !PINNED_ORDER.includes(v))
    .sort((a, b) => sideLabel(a).localeCompare(sideLabel(b)));
  return [
    { value: ALL_SIDES, label: 'All sides' },
    ...[...pinned, ...others].map(value => ({ value, label: sideLabel(value) })),
    { value: NO_SIDE, label: 'No side' },
  ];
}

export function matchesSideFilter(guest: WithSide, filter: string): boolean {
  if (filter === ALL_SIDES) return true;
  const side = normalizeSide(guest.side as string | null | undefined);
  if (filter === NO_SIDE) return side === null;
  return side === filter;
}
